/**
 * Juntar a assinatura do operador ao texto da ação do ticket.
 *
 * ## Por que isto é um módulo, e não três linhas na tela
 *
 * Porque a ação é HTML do Quill, e "juntar texto" aqui tem quatro casos que a
 * tela erraria um a um: ação vazia, ação que já termina com a assinatura
 * (salvar duas vezes), assinatura com várias linhas, e assinatura nula.
 * Tendo um módulo, cada caso tem teste e a tela tem uma chamada.
 *
 * ## A assinatura é TEXTO, e vira HTML aqui
 *
 * A api guarda texto puro (`strip_tags` na entrada) justamente para que nenhum
 * ponto de leitura precise sanitizar. Então quem converte para HTML é este
 * módulo, e ele escapa o que converte -- um `&` ou um `<` no nome de alguém
 * não pode virar marcação.
 */

/**
 * O marcador que separa a ação da assinatura.
 *
 * Existe para `jaTemAssinatura()` reconhecer o próprio trabalho sem comparar
 * o texto inteiro: quem edita a assinatura antes de salvar faria a comparação
 * literal falhar, e a assinatura entraria duas vezes.
 *
 * ## O marcador NÃO sobrevive à gravação -- medido em 14/10/2026
 *
 * Salvei uma ação pela tela e fui ler no banco: a api guarda
 * `<p>Matheus Torres<br />...</p>`, **sem o atributo**. Alguma sanitização do
 * lado do servidor o remove.
 *
 * Então o reconhecimento vale só para conteúdo DESTA sessão, antes de ir ao
 * servidor -- e é por isso que ele não é o que impede assinatura dupla hoje. O
 * que impede são duas coisas de desenho:
 *
 * - a assinatura é anexada no ENVIO, a partir do que está no editor, e o
 *   editor nunca a contém (ver `handleAddComment` no `TicketDetails`);
 * - editar uma ação existente passa por outro handler, que não assina -- então
 *   reeditar e salvar não acrescenta uma segunda.
 *
 * O guard fica porque custa nada e protege o dia em que alguém decidir inserir
 * a assinatura dentro do editor. Mas não dá para contar com ele para conteúdo
 * que já foi gravado.
 */
export const MARCADOR_DA_ASSINATURA = 'data-assinatura="operador"';

const escaparHtml = (texto) => String(texto)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/**
 * A IMAGEM só entra se for um endereço do nosso próprio storage.
 *
 * A api devolve a URL que ELA gerou ao gravar o arquivo (`/storage/...`), e é
 * só isso que este módulo aceita montar. O valor vem do payload do usuário, e
 * um `src` arbitrário dentro do HTML da ação seria conteúdo de terceiro
 * entrando no chamado -- `javascript:` e `data:` em primeiro lugar.
 *
 * Aceita caminho relativo e URL absoluta (a api devolve absoluta, mas um
 * ambiente servido por outro domínio devolveria relativa).
 */
const imagemSegura = (url) => {
    const limpa = String(url || '').trim();

    if (!limpa) return null;
    if (limpa.startsWith('/storage/')) return limpa;

    return /^https?:\/\/[^/]+\/storage\//i.test(limpa) ? limpa : null;
};

/**
 * A assinatura em HTML, como um bloco só, com as quebras preservadas.
 *
 * A imagem (pedido da Gisele, 19/10/2026) vai DEPOIS do texto, em linha
 * própria: é assim que assinatura de e-mail se lê -- nome, cargo, telefone, e
 * então o logo. `max-width` em linha porque o HTML da ação é lido fora daqui
 * (no e-mail, por exemplo), onde o CSS da nossa tela não existe.
 */
export const assinaturaComoHtml = (assinatura, imagem = null) => {
    const limpa = String(assinatura || '').trim();
    const src = imagemSegura(imagem);

    if (!limpa && !src) return '';

    const linhas = limpa
        ? limpa.split(/\r?\n/).map((l) => escaparHtml(l)).join('<br>')
        : '';

    const figura = src
        ? `${linhas ? '<br>' : ''}<img src="${escaparHtml(src)}" alt="Assinatura" style="max-width:300px;max-height:120px;">`
        : '';

    return `<p ${MARCADOR_DA_ASSINATURA}>${linhas}${figura}</p>`;
};

/** O texto já carrega uma assinatura colocada por aqui? */
export const jaTemAssinatura = (html) => String(html || '').includes(MARCADOR_DA_ASSINATURA);

/**
 * O conteúdo da caixa de ação com a assinatura no fim.
 *
 * Devolve o texto INTACTO quando não há assinatura para pôr, ou quando ela já
 * está lá -- nunca acrescenta duas.
 */
export const comAssinatura = (html, assinatura, imagem = null) => {
    const bloco = assinaturaComoHtml(assinatura, imagem);

    if (!bloco) return String(html || '');
    if (jaTemAssinatura(html)) return String(html || '');

    const atual = String(html || '').trim();

    // Ação vazia recebe só a assinatura, sem o parágrafo em branco na frente:
    // o Quill devolve `<p><br></p>` para "vazio", e tratá-lo como conteúdo
    // deixaria uma linha morta no começo de toda ação.
    if (!atual || atual === '<p><br></p>') return bloco;

    return `${atual}${bloco}`;
};

/**
 * O conteúdo SEM a assinatura, para quando o operador desmarca a caixa.
 *
 * Remove o bloco que este módulo colocou, e só ele. Texto que o operador
 * digitou por conta própria nunca casa com o marcador.
 */
export const semAssinatura = (html) => String(html || '')
    .replace(new RegExp(`<p ${MARCADOR_DA_ASSINATURA}>.*?</p>`, 'gs'), '')
    .trim();
